import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  // localStorage'dan kullanıcı bilgilerini başlangıçta al
  const storedUser = localStorage.getItem('user');
  const initialUser = storedUser ? JSON.parse(storedUser) : null;
  
  const [user, setUser] = useState(initialUser);
  const [loading, setLoading] = useState(true);
  const [setupChecked, setSetupChecked] = useState(false);
  const [setupData, setSetupData] = useState(null);

  // Uygulama başladığında setup kontrolü yap
  useEffect(() => {
    checkSetup();
  }, []);

  // Token varsa kullanıcı bilgilerini getir
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token && setupData?.hasUsers) {
      // Kullanıcı bilgilerini API'den güncelle
      getCurrentUser();
    } else {
      setLoading(false);
    }
  }, [setupData]);

  // Setup durumunu kontrol et
  const checkSetup = async () => {
    try {
      const response = await authAPI.checkSetup();
      setSetupData(response.data);
      setSetupChecked(true);
    } catch (error) {
      console.error('Setup kontrolü hatası:', error);
      setSetupChecked(true);
    }
  };

  // Mevcut kullanıcı bilgilerini getir
  const getCurrentUser = async () => {
    try {
      const response = await authAPI.getMe();
      setUser(response.data.user);
      // Güncel kullanıcı bilgilerini localStorage'a kaydet
      localStorage.setItem('user', JSON.stringify(response.data.user));
    } catch (error) {
      console.error('Kullanıcı bilgisi alma hatası:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  // Giriş
  const login = async (credentials) => {
    try {
      const response = await authAPI.login(credentials);
      const { token, user: userData } = response.data;
      
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userData));
      
      // State'i hemen güncelleme, toast görününce sonra güncelle
      setTimeout(() => {
        setUser(userData);
      }, 1500); // 1.5 saniye sonra yönlendir
      
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.error || 'Giriş hatası' 
      };
    }
  };

  // Kayıt
  const register = async (userData) => {
    try {
      const response = await authAPI.register(userData);
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.error || 'Kayıt hatası' 
      };
    }
  };

  // Çıkış
  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  // Şifre değiştir
  const changePassword = async (passwordData) => {
    try {
      const response = await authAPI.changePassword(passwordData);
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.error || 'Şifre değiştirme hatası' 
      };
    }
  };

  // Admin: Normal kullanıcı girişi aç/kapat
  const toggleLogin = async (enabled) => {
    try {
      const response = await authAPI.toggleLogin(enabled);
      await checkSetup();
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Ayar güncelleme hatası'
      };
    }
  };

  // Admin: Register aç/kapat
  const toggleRegister = async (enabled) => {
    try {
      const response = await authAPI.toggleRegister(enabled);
      // Setup data'yı güncelle
      await checkSetup();
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.error || 'Ayar güncelleme hatası' 
      };
    }
  };

  // Admin: Yeni kullanıcı oluştur
  const createUser = async (userData) => {
    try {
      const response = await authAPI.createUser(userData);
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.error || 'Kullanıcı oluşturma hatası' 
      };
    }
  };

  // Admin: Kullanıcıları getir
  const getUsers = async () => {
    try {
      const response = await authAPI.getUsers();
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.error || 'Kullanıcı listesi alma hatası' 
      };
    }
  };

  // Admin: Kullanıcı güncelle
  const updateUser = async (userId, userData) => {
    try {
      const response = await authAPI.updateUser(userId, userData);
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.error || 'Kullanıcı güncelleme hatası' 
      };
    }
  };

  // Admin: Kullanıcı sil
  const deleteUser = async (userId) => {
    try {
      const response = await authAPI.deleteUser(userId);
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.error || 'Kullanıcı silme hatası' 
      };
    }
  };

  // Admin: Kullanıcı aktif/pasif durumunu değiştir
  const toggleUserActive = async (userId, isActive) => {
    try {
      const response = await authAPI.toggleUserActive(userId, isActive);
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.error || 'Kullanıcı durumu değiştirme hatası' 
      };
    }
  };

  const value = {
    user,
    loading,
    setupChecked,
    setupData,
    login,
    register,
    logout,
    changePassword,
    toggleLogin,
    toggleRegister,
    createUser,
    getUsers,
    updateUser,
    deleteUser,
    toggleUserActive,
    checkSetup,
    isAuthenticated: !!user,
    isAdmin: user?.isAdmin || false,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
